import React, { useState } from 'react';
import { EMAIL_CATEGORIES } from './constants';
import { ResponseStatus } from '../services/common/types';
import { useMessageService } from '../services/message';
import { escapeHtml } from '../common/utils';

const SUPPORT_INBOX_EMAIL = 'accounts.theezy+support@gscbinc.com';

interface SupportFormProps {
    onSuccess?: () => void;
}

export const SupportForm = ({ onSuccess }: SupportFormProps) => {
    const { sendEmail } = useMessageService();
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [category, setCategory] = useState<string>(EMAIL_CATEGORIES[0]);
    const [title, setTitle] = useState('');
    const [message, setMessage] = useState('');
    const [status, setStatus] = useState<string | undefined>();
    const [error, setError] = useState<string | null>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!name || !email || !title || !message) {
            setError('Please fill out all fields before sending.');
            return;
        }

        setStatus('submitting');
        setError(null);

        const content = [
            `Name: ${escapeHtml(name)}`,
            `Email: ${escapeHtml(email)}`,
            `Category: ${escapeHtml(category)}`,
            `Submitted: ${new Date().toLocaleString()}`,
            '',
            escapeHtml(message).replace(/\n/g, '<br>'),
        ].join('<br>');

        const data = await sendEmail({
            from: email,
            to: SUPPORT_INBOX_EMAIL,
            subject: `${category}: ${title}`,
            content,
        });

        if (data.status === ResponseStatus.ERROR) {
            setStatus(undefined);
            setError(`Something went wrong sending your message. Please try again or email us directly at ${SUPPORT_INBOX_EMAIL}`);
            return;
        }

        setStatus('success');
        setName('');
        setEmail('');
        setCategory(EMAIL_CATEGORIES[0]);
        setTitle('');
        setMessage('');
        setTimeout(() => onSuccess?.(), 2500);
    };

    return status === 'success' ? (
        <div className="survey-success">
            <span>✓</span>
            <p>Thanks for reaching out, we'll follow up by email shortly.</p>
        </div>
    ) : (
        <form className="support-form" onSubmit={handleSubmit}>
            <div className="support-field">
                <label htmlFor="support-name">Name</label>
                <input
                    id="support-name"
                    className="support-input"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    disabled={status === 'submitting'}
                />
            </div>

            <div className="support-field">
                <label htmlFor="support-email">Email</label>
                <input
                    id="support-email"
                    className="support-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={status === 'submitting'}
                />
            </div>

            <div className="support-field">
                <label>Category</label>
                <div className="support-category-options">
                    {EMAIL_CATEGORIES.map((c) => (
                        <button
                            type="button"
                            key={c}
                            className={`survey-option ${category === c ? 'survey-option--selected' : ''}`}
                            onClick={() => setCategory(c)}
                            disabled={status === 'submitting'}
                        >
                            {c}
                        </button>
                    ))}
                </div>
            </div>

            <div className="support-field">
                <label htmlFor="support-title">Subject</label>
                <input
                    id="support-title"
                    className="support-input"
                    type="text"
                    placeholder="Brief summary of your issue"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    disabled={status === 'submitting'}
                />
            </div>

            <div className="support-field">
                <label htmlFor="support-message">How can we help?</label>
                <textarea
                    id="support-message"
                    className="survey-textarea"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Your message..."
                    maxLength={1000}
                    rows={5}
                    disabled={status === 'submitting'}
                />
            </div>

            {error && <p className="survey-error">{error}</p>}

            <div className="support-form-footer">
                <button type="submit" className="survey-submit-btn" disabled={status === 'submitting'}>
                    {status === 'submitting' ? 'Sending...' : 'Send message'}
                </button>
            </div>
        </form>
    );
};

export default SupportForm;